import { AxiosResponse } from 'axios';

import { isV2Auth } from '@/config/auth.config';
import { Axios, injectAxios } from '@/lib/axios/Axios';
import { tokenInterceptor } from '@/lib/axios/interceptors/token.interceptor';

import { AuthorizationResponse } from '../types';

const REQUEST_TIMEOUT = 60000;

const AUTH_ENDPOINTS = {
  v1: '/1.0/authorization',
  v2: '/2.0/auth/user/me',
} as const;

class AuthRequests {
  public static get axiosInstance(): Axios {
    return this._axiosInstance;
  }

  private static readonly _axiosInstance = injectAxios(
    `${ process.env.NEXT_PUBLIC_API_HOST }`,
    REQUEST_TIMEOUT,
    { withCredentials: true },
  ).useRequestInterceptor(config => tokenInterceptor(config, {}));

  private static get authEndpoint(): string {
    return isV2Auth() ? AUTH_ENDPOINTS.v2 : AUTH_ENDPOINTS.v1;
  }

  /**
   * Authenticates with a token.
   * V1: GET /1.0/authorization (legacy)
   * V2: GET /2.0/auth/user/me
   */
  static authorization(token: string) {
    const tokenToUse = Array.isArray(token) ? token[0] : token;

    return this._axiosInstance
      .useTokenInterceptor(tokenToUse)
      .get<AuthorizationResponse>(this.authEndpoint, {
        withCredentials: isV2Auth(),
        retry: 3,
      });
  }

  /**
   * Authenticates using cookies (V2 only).
   * Calls /2.0/auth/user/me with withCredentials.
   * In V1 mode, returns 401 to force fallback to stored token.
   */
  static authorizationWithCookies() {
    if (!isV2Auth()) {
      return Promise.resolve({ status: 401 } as unknown as AxiosResponse<AuthorizationResponse>);
    }

    return this._axiosInstance.get<AuthorizationResponse>(this.authEndpoint, {
      withCredentials: true,
      retry: 3,
    });
  }
}

export default AuthRequests;
